import { padStartTwoDigits as pad } from './utils.string';

export interface extractDateProps {
    date: number;
    month: number;
    year: number;
    hours: number;
    minutes: number;
    seconds: number;
}

export type DateFormat = 'date' | 'date-time' | 'date-time-sec' | 'time' | 'time-sec';

export const extractDate = (str?: string | Date): extractDateProps => {
    const dateJs = str ? new Date(str) : new Date();
    return {
        date: dateJs.getDate(),
        month: dateJs.getMonth() + 1,
        year: dateJs.getFullYear(),
        hours: dateJs.getHours(),
        minutes: dateJs.getMinutes(),
        seconds: dateJs.getSeconds(),
    };
};

/**
 * Format date to YYYY-MM-DD hh:mm:ss format
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateTime = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const isSameDay = (d1: string, d2: string) => {
    const { date: date2, month: month2, year: year2 } = extractDate(d2);
    const { date, month, year } = extractDate(d1);

    return !!(date2 === date && month2 === month && year2 === year);
};

export const isToday = (datetime: string) => {
    const { date: currentDate, month: currentMonth, year: currentYear } = extractDate();
    const { date, month, year } = extractDate(datetime);

    return !!(currentDate === date && currentMonth === month && currentYear === year);
};

export const isYesterday = (datetime: string) => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const { date: yesterdayDate, month: yesterdayMonth, year: yesterdayYear } = extractDate(yesterday);
    const { date, month, year } = extractDate(datetime);

    return !!(date === yesterdayDate && month === yesterdayMonth && year === yesterdayYear);
};

export const isTomorrow = (datetime: string) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const { date: tomorrowDate, month: tomorrowMonth, year: tomorrowYear } = extractDate(tomorrow);
    const { date, month, year } = extractDate(datetime);

    return !!(date === tomorrowDate && month === tomorrowMonth && year === tomorrowYear);
};

export const dateLabel = (date: string) => {
    if (isToday(date)) return 'GLOBAL_Today';
    if (isYesterday(date)) return 'GLOBAL_Yesterday';
    if (isTomorrow(date)) return 'GLOBAL_Tomorrow';
    return null;
};

/**
 * Convert date string to timestamp
 * @param {string} str - Date format Y-m-d H:i:s
 * @returns {(undefined|number)} Return timestamp
 */
export const strToTimestamp = (str: string): undefined | number => {
    if (str) {
        const timestamp = Date.parse(str);
        if (!isNaN(timestamp)) {
            return new Date(timestamp).getTime() / 1000;
        }
    }
    return;
};

/**
 * This method returns the date without the time difference (jet lag)!!
 * @param date date
 * @returns ISODate
 */
export const toISODate = (date = new Date()): string => date.toISOString().substring(0, 10);

export const toHumanDate = (date: extractDateProps, format: DateFormat = 'date') => {
    const formatArr = format.split('-');
    const displayDate = [];

    if (formatArr.includes('date')) {
        displayDate.push(`${pad(date.date)}/${pad(date.month)}/${date.year}`);
    }
    if (formatArr.includes('time')) {
        displayDate.push(`${pad(date.hours)}h${pad(date.minutes)}`);
    }
    if (formatArr.includes('sec')) {
        displayDate.push(`${pad(date.seconds)}s`);
    }

    return displayDate.join(' ');
};

export const isValidDate = (date: string) => {
    return !isNaN(Date.parse(date));
};

export const substractMonth = (date: Date) => {
    const result = new Date(date);
    result.setDate(0);
    if (date.getDate() < result.getDate()) {
        result.setDate(date.getDate());
    }
    return result;
};

export const addMonth = (date: Date) => {
    const result = new Date(date);
    // We actually can't get the last day of a month,
    // so we force adding two month and we remove on because setDate(0) set the day to the latest day of the previous month
    result.setDate(32);
    result.setDate(32);
    result.setDate(0);
    if (date.getDate() < result.getDate()) {
        result.setDate(date.getDate());
    }
    return result;
};
